import React from 'react'
import { X } from 'lucide-react'
import type { HealthV2Metric } from './HealthCashFlowSection'

// SPS-5568: "Break down my score" side panel. Rebuilt to the approved mockup
// (Health Score Widget): a single-column, right-edge slide-in drawer (no gauge),
// horizontal score-bands scale, tonal metric bars, neutral weight badges, Total row.

export interface HealthScoreDrawerProps {
  isOpen: boolean
  onClose: () => void
  score: number
  scoreChange?: number | null
  avgVerticalScore?: number | null
  verticalRetailerCount?: number | null
  verticalPercentile?: number | null
  /** What the benchmarks are, e.g. "the top 25% of Outdoor retailers". */
  benchmarkBasis?: string
  vertical: string
  clientLabel?: string
  throughLabel: string
  metrics: HealthV2Metric[]
  imu?: { actual: number; benchmark: number | null; actualDisplay: string; benchmarkDisplay: string } | null
}

// Band scale (mockup BANDS) — hexes and ranges are the mockup's, used for the score
// band pill, the horizontal bands scale, and each metric's performance band.
const BANDS = [
  { name: 'Poor', min: 0, max: 39, color: '#F04438' },
  { name: 'Fair', min: 40, max: 54, color: '#F97316' },
  { name: 'Average', min: 55, max: 69, color: '#EAB308' },
  { name: 'Good', min: 70, max: 84, color: '#84CC16' },
  { name: 'Excellent', min: 85, max: 100, color: '#22C55E' },
]
type Band = typeof BANDS[number]

function bandFor(pct: number): Band {
  const p = Math.round(pct)
  return BANDS.find((b) => p >= b.min && p <= b.max) || BANDS[0]
}

// tonal(): the filled segments ramp light→dark across the band color; the rest grey.
// Mirrors the mockup's oklch color-mix ramp exactly.
function tonalSegments(band: Band): string[] {
  const n = BANDS.indexOf(band) + 1
  const segs = Array(5).fill('#EAECF0')
  for (let i = 0; i < n; i++) {
    if (n === 1) { segs[i] = band.color; continue }
    const t = i / (n - 1)
    const v = -55 + t * (55 + (n - 2) * 12)
    segs[i] = v < 0
      ? `color-mix(in oklch, ${band.color}, #fff ${Math.round(-v)}%)`
      : v > 0
        ? `color-mix(in oklch, ${band.color}, #101828 ${Math.round(v)}%)`
        : band.color
  }
  return segs
}

// Per-metric copy from the approved mockup (keyed by HealthMetricKey).
const METRIC_DESC: Record<string, string> = {
  grossMargin: 'What you keep from every sale after cost of goods. Your biggest lever for profit.',
  salesGrowth: 'How your revenue this year compares to last year, against stores your size in your vertical.',
  markdowns: 'Share of sales sold at a discount. Lower is better — high markdowns usually mean overbuying or slow sell-through.',
  turn: 'How many times you sell through your inventory in a year. Faster turns mean fresher product and steadier cash flow.',
  freshness: 'Share of inventory received in the last 90 days. Fresher inventory sells better.',
}

// "Why you're here": the action line is the weakest metric's advice, keyed by HealthMetricKey.
const METRIC_ACTION: Record<string, string> = {
  grossMargin: 'Review vendor cost and pricing before your next buy to protect this margin.',
  salesGrowth: "Focus on driving traffic and repeat purchases. Given this metric's weight, even a partial gain here moves your score the most.",
  markdowns: 'Keep discount depth and timing disciplined to hold this line.',
  turn: 'Clear slow-moving stock faster to lift your turn rate.',
  freshness: 'Bring in new receipts more often to keep the floor feeling fresh.',
}

function formatPoints(points: number) {
  return Number.isInteger(points) ? String(points) : points.toFixed(1)
}

export function HealthScoreDrawer({
  isOpen,
  onClose,
  score = 0,
  vertical,
  throughLabel,
  metrics,
  imu = null,
}: HealthScoreDrawerProps) {
  React.useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isOpen, onClose])

  if (!isOpen) return null

  const scoreBand = BANDS.find((b) => score >= b.min && score <= b.max) || BANDS[BANDS.length - 1]
  const verticalLabel = vertical || 'vertical'

  // Horizontal score-bands scale: each band's width is flex-proportional to its range,
  // and the "You · {score}" marker sits at the score's position along that scale.
  const totalUnits = BANDS.reduce((sum, b) => sum + (b.max - b.min + 1), 0)
  let unitsBefore = 0
  for (const b of BANDS) { if (b === scoreBand) break; unitsBefore += (b.max - b.min + 1) }
  const scorePct = `${((unitsBefore + (score - scoreBand.min)) / totalUnits) * 100}%`

  // Per-metric rows: performance band = points earned as a % of the metric's weight.
  const rows = metrics.map((metric) => {
    const perfPct = metric.maxPoints ? (metric.points / metric.maxPoints) * 100 : 0
    const band = bandFor(perfPct)
    const isHeaviest = metric.maxPoints === Math.max(...metrics.map((m) => m.maxPoints))
    return {
      metric,
      band,
      segs: tonalSegments(band),
      weightLabel: `Weight ${metric.maxPoints}${isHeaviest ? ' · heaviest' : ''}`,
      desc: METRIC_DESC[metric.key] || metric.description,
      ptsText: `${formatPoints(metric.points)} of ${metric.maxPoints} pts`,
      perfPct,
    }
  })

  // "Why you're here": sort by weight earned; strongest = best ratio, weakest = worst.
  const ranked = [...rows].sort((a, b) => b.perfPct - a.perfPct)
  const strongest = ranked[0]
  const weakest = ranked[ranked.length - 1]
  const why = (strongest && weakest && strongest !== weakest)
    ? {
      strong: {
        name: strongest.metric.label,
        text: `${strongest.band.name.toLowerCase()} at ${strongest.metric.actualDisplay} against a ${strongest.metric.benchmarkDisplay} benchmark.`,
      },
      weak: {
        name: weakest.metric.label,
        text: `${weakest.band.name.toLowerCase()} at ${weakest.metric.actualDisplay} vs ${weakest.metric.benchmarkDisplay}, and it carries ${weakest.metric.maxPoints} of the 100 points.`,
      },
      action: METRIC_ACTION[weakest.metric.key] || '',
    }
    : null

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-xs animate-in fade-in duration-200">
      <div className="fixed inset-0" onClick={onClose} aria-hidden="true" />

      <div
        className="fixed inset-y-0 right-0 z-10 flex w-full max-w-[560px] flex-col overflow-hidden border-l border-[#EAECF0] bg-white shadow-2xl animate-in slide-in-from-right duration-200"
        style={{ fontFamily: "'Inter', system-ui, sans-serif", color: '#101828' }}
      >
        {/* Header */}
        <div className="flex items-start gap-4 border-b border-[#EAECF0] px-6 pb-5 pt-5">
          <div className="flex flex-col gap-2">
            <div className="text-[11px] font-semibold tracking-[0.04em] text-[#5558D4]">RetailORBIT Health Score™</div>
            <div className="text-[24px] font-semibold leading-tight tracking-[-0.015em]">Your score, broken down</div>
            <div className="text-[13px] text-[#667085]">
              Trailing 12 months through {throughLabel}
              {vertical ? ` · ${vertical} benchmarks` : ''}
            </div>
          </div>
          <span className="flex-grow" />
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="flex h-9 w-9 flex-none items-center justify-center rounded-lg border border-[#EAECF0] bg-white text-[#667085] hover:bg-slate-50"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex min-h-0 flex-1 flex-col gap-8 overflow-y-auto px-6 py-6">
          {/* Score + band pill + intro */}
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <div className="flex items-baseline gap-1">
                <span className="text-[40px] font-semibold leading-none tracking-[-0.02em]">{score}</span>
                <span className="text-sm text-[#667085]">/100</span>
              </div>
              <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-[#EAECF0] bg-white px-2.5 py-0.5 text-[13px] font-semibold text-[#344054]">
                <span className="h-2 w-2 rounded-full" style={{ background: scoreBand.color }} />
                {scoreBand.name}
              </span>
            </div>
            <p className="text-sm leading-[1.6] text-[#475467]">
              Your score is a single number from 0 to 100 that measures your store’s financial health over the last 12 months.
              Each of five metrics is compared with the {verticalLabel} benchmark and earns points up to its weight.
              The points below add up to your score.
            </p>
          </div>

          {/* Why you're here, and what to do */}
          {why && (
            <div className="flex flex-col gap-3">
              <div className="text-[11px] font-semibold tracking-[0.04em] text-[#667085]">Why you’re here, and what to do</div>
              <div className="flex flex-col gap-2 rounded-xl border border-[#EAECF0] bg-[#F9FAFB] px-[18px] py-4 text-sm leading-[1.55] text-[#475467]">
                <p><strong className="font-semibold text-[#101828]">{why.strong.name}</strong> is your strongest metric right now: {why.strong.text}</p>
                <p><strong className="font-semibold text-[#101828]">{why.weak.name}</strong> is your biggest opportunity: {why.weak.text}</p>
                {why.action && <p className="text-[#101828]">{why.action}</p>}
              </div>
            </div>
          )}

          {/* Score bands — horizontal scale with "You · {score}" marker */}
          <div className="flex flex-col gap-3">
            <div className="text-[11px] font-semibold tracking-[0.04em] text-[#667085]">Score bands</div>
            <div className="relative pt-7">
              <div
                className="absolute top-0 flex flex-col items-center"
                style={{ left: scorePct, transform: 'translateX(-50%)' }}
              >
                <span className="whitespace-nowrap rounded px-1.5 py-0.5 text-[12px] font-semibold text-white" style={{ background: '#101828' }}>
                  You · {score}
                </span>
                <span className="h-2 w-0.5" style={{ background: '#101828' }} />
              </div>
              <div className="flex h-2 gap-[3px]">
                {BANDS.map((b) => (
                  <div key={b.name} className="rounded-[3px]" style={{ background: b.color, flex: b.max - b.min + 1 }} />
                ))}
              </div>
              <div className="mt-2 flex gap-[3px]">
                {BANDS.map((b) => {
                  const isCurrent = b === scoreBand
                  return (
                    <div key={b.name} className="flex min-w-0 flex-col gap-0.5" style={{ flex: b.max - b.min + 1 }}>
                      <span className="text-[12px]" style={{ color: isCurrent ? '#101828' : '#98A2B3', fontWeight: isCurrent ? 600 : 400 }}>{b.name}</span>
                      <span className="text-[12px] text-[#98A2B3]">{b.min}–{b.max}</span>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>

          {/* How your score is calculated */}
          <div className="flex flex-col gap-4">
            <div className="text-[11px] font-semibold tracking-[0.04em] text-[#667085]">How your score is calculated</div>

            {rows.map(({ metric, band, segs, weightLabel, desc, ptsText }) => (
              <div key={metric.key} className="flex flex-col gap-3 rounded-2xl border border-[#EAECF0] p-5">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-base font-semibold text-[#101828]">{metric.label}</span>
                    <span className="rounded-md bg-[#F2F4F7] px-2 py-0.5 text-[11px] font-semibold text-[#475467]">{weightLabel}</span>
                  </div>
                  <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-[#EAECF0] bg-white px-2 py-0.5 text-[12px] font-medium text-[#344054]">
                    <span className="h-2 w-2 rounded-full" style={{ background: band.color }} />
                    {band.name}
                  </span>
                </div>

                <div className="text-sm leading-[1.5] text-[#667085]">{desc}</div>

                <div className="flex items-baseline justify-between gap-3 text-sm text-[#667085]">
                  <span className="flex items-baseline gap-2 whitespace-nowrap">
                    <span className="text-base font-semibold text-[#101828]">{metric.actualDisplay}</span>
                    <span className="text-[#D0D5DD]">·</span>
                    <span>Benchmark <span className="font-semibold text-[#344054]">{metric.benchmarkDisplay}</span></span>
                  </span>
                  <span className="font-semibold text-[#101828]">{ptsText}</span>
                </div>

                <div className="flex h-2 gap-1">
                  {segs.map((color, i) => (
                    <div key={i} className="flex-1 rounded-[3px]" style={{ background: color }} />
                  ))}
                </div>
              </div>
            ))}

            {/* IMU — diagnostic row, not scored */}
            {imu && (
              <div className="flex flex-col gap-3 rounded-2xl border border-dashed border-[#D0D5DD] p-5">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-base font-semibold text-[#667085]">IMU</span>
                    <span className="rounded-md bg-[#F2F4F7] px-2 py-0.5 text-[11px] font-semibold text-[#475467]">Diagnostic</span>
                  </div>
                  <span className="inline-flex items-center whitespace-nowrap rounded-full border border-dashed border-[#D0D5DD] bg-white px-2 py-0.5 text-[12px] font-medium text-[#667085]">
                    Not scored
                  </span>
                </div>
                <div className="text-sm leading-[1.5] text-[#667085]">
                  Initial markup on what you received. Shown to explain gross margin alongside markdowns. Not part of your score.
                </div>
                <div className="flex items-baseline gap-2 text-sm text-[#667085]">
                  <span className="text-base font-semibold text-[#475467]">{imu.actualDisplay}</span>
                  <span className="text-[#D0D5DD]">·</span>
                  <span>Benchmark <span className="font-semibold text-[#344054]">{imu.benchmarkDisplay}</span></span>
                </div>
              </div>
            )}

            {/* Total */}
            <div className="flex items-baseline justify-between px-5 pt-1">
              <span className="text-base font-semibold">Total</span>
              <span className="text-[20px] font-semibold">{score} of 100 pts</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-[#EAECF0] bg-[#F9FAFB] px-6 py-4 text-[12px] text-[#667085]">
          Scores update monthly · Data through {throughLabel}
        </div>
      </div>
    </div>
  )
}
